<template>
  <div>
    <div class="mb-3">
      <router-link to="/work" class="btn btn-secondary btn-no-r">
        <span><BaseIcon name="active" /> {{ $t("work.dashboard") }}</span>
      </router-link>
      <router-link to="/work/tracking/history" class="btn btn-no-l btn-primary">
        <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
      </router-link>
    </div>
    <div class="white-box-half pt-2" v-if="data.active?.length > 0">
      <div
        class="row mx-2 my-1 align-items-center"
        v-for="row in data.active"
        :key="row.id"
      >
        <div class="col-2">
          <span class="badge badge-outline">{{ row.bucket }}</span>
        </div>
        <div class="col-2">
          <span class="badge badge-outline">{{ row.group }}</span>
        </div>
        <div class="col text-secondary" v-if="row.is_automatic">
          Automatic tracking from ActivityWatch
        </div>
        <div class="col" v-else>
          {{ row.name ?? "Not categorized" }}
        </div>
        <div class="col-1">
          <span class="bold-19" :key="timeKey">
            {{ formatTime(elapsedTime(row.started_at), true) }}
          </span>
        </div>
        <div class="col-auto" v-if="!row.is_automatic">
          <button
            class="btn btn-transparent"
            v-on:click="saveActiveTime(row.id)"
          >
            <BaseIcon name="plus-circle" />
          </button>
        </div>
      </div>
    </div>
    <div v-else class="text-start custom-input m-2">
      <div class="row">
        <div class="col-12 mb-3 whitebg-input">
          <div class="divided-input">
            <v-select
              placeholder="What are you working on?"
              v-model="newItem"
              :options="tasks"
              taggable
              :filterable="false"
              v-on:search="searchTask"
              :create-option="(v) => createOption(v)"
              v-on:option:selected="(v) => selectOption(v)"
              label="name"
              class="flex-2-1-0"
              append-to-body
            >
              <template v-slot:option="option">
                <span class="me-2">
                  {{ option.name }}
                </span>
                <span class="badge badge-outline ms-2">{{
                  option.bucket
                }}</span>
                <span class="badge badge-outline ms-2">{{ option.group }}</span>
              </template>
            </v-select>
            <v-select
              v-if="newItem?.id === 0"
              placeholder="Select a bucket"
              v-model="selectedBucket"
              :options="data.buckets"
              label="name"
              :reduce="(c) => c.id"
              append-to-body
            />
            <div v-else-if="newItem?.id > 0" span="p-3">
              {{ selectedBucket.name }}
            </div>
            <v-select
              v-if="newItem?.id === 0 && selectedBucket > 0"
              placeholder="Select a group"
              v-model="selectedGroup"
              :options="groups"
              label="name"
              :reduce="(c) => c.id"
              append-to-body
            />
            <div v-else-if="selectedBucket?.id > 0" span="p-3">
              {{ selectedGroup.name }}
            </div>
            <input
              type="time"
              v-if="manualAdd"
              v-model="duration"
              class="form-control duration-input"
            />
            <div class="custom-input-end" v-if="manualAdd">
              <button class="btn btn-link" v-on:click="manualAdd = false">
                <BaseIcon name="close-circle" />
                Close
              </button>
              <button class="btn btn-primary" v-on:click="saveTime(true)">
                Save <BaseIcon name="plus" />
              </button>
            </div>
            <div class="custom-input-end" v-else>
              <button class="btn btn-link" v-on:click="manualAdd = true">
                <BaseIcon name="plus" />
                Add manually
              </button>
              <button class="btn btn-primary" v-on:click="saveTime">
                Start <BaseIcon name="timer-start" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="white-box" v-for="week in weeks" :key="week">
      <div class="d-flex justify-content-evenly week-header">
        <div
          v-for="day in week"
          :key="day"
          class="text-center time-day"
          :class="{ 'time-today': today === day.date }"
        >
          <div class="text-muted small">{{ day.day }}</div>
          <div class="fw-bold">
            {{ formatDate(day.date) }}
          </div>
          <div class="bold-24">
            {{ formatTime(data.duration[day.date] ?? 0) }}
          </div>
        </div>
      </div>
      <template v-for="day in Object.values(week).reverse()" :key="day">
        <div
          class="p-3"
          v-if="data.rows[day.date]?.length > 0 || today === day.date"
        >
          <h2 class="semibold-16 mb-3">{{ day.day }}</h2>
          <div
            v-for="row in data.rows[day.date]"
            :key="row"
            class="tracked-session"
            :class="{
              assigned: row.type === 'task',
              unassigned: row.type === 'bucket',
            }"
            v-on:click="showTimeAllocator(row)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <div class="session-title">
                  {{ row.bucket }}
                </div>
                <div class="session-description small text-muted">
                  {{
                    row.name ??
                    "Automatic tracking from ActivityWatch - Click to assign to tasks"
                  }}
                </div>
                <div class="session-meta mt-1">
                  <span class="text-muted me-3">
                    {{ timeRange(row) }}
                  </span>
                  <div class="session-tag" v-if="row.group_name">
                    {{ row.group_name }}
                  </div>
                </div>
              </div>
              <div class="text-end">
                <div class="bold-19">
                  {{ formatTime(row.duration) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <TimeAllocatorModal ref="timeAllocatorModal" v-model="selectedRow" @saved="refresh" />
  </div>
</template>

<script>
import http from "@/modules/http";
import date from "../../../modules/date";
import BaseIcon from "../../../components/icons/BaseIcon.vue";
import TimeAllocatorModal from "../../../components/modals/TimeAllocatorModal.vue";

export default {
  name: "LocalTiming",
  components: { TimeAllocatorModal, BaseIcon },
  data() {
    return {
      data: undefined,
      tasks: [],
      newItem: null,
      selectedBucket: null,
      groups: [],
      selectedGroup: null,
      selectedRow: null,
      duration: 0,
      timeKey: 0,
      manualAdd: false,
    };
  },
  computed: {
    today() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    weeks() {
      if (this.data === undefined) {
        return 0;
      }
      let keys = Object.keys(this.data.duration);
      let firstMonday = this.getNextMonday(keys[0]);
      let start = keys.findIndex((e) => e === firstMonday);
      let weeks = this.getWeeksBetweenDates(firstMonday, keys[keys.length - 1]);
      return Array.from({ length: weeks }, (_, index) =>
        this.getWeekDatesWithDayNames(keys[start + index * 7])
      ).reverse();
    },
  },
  mounted() {
    this.load();
    setInterval(() => this.timeKey++, 1010);
  },
  watch: {
    selectedBucket() {
      http
        .fetch("/work/buckets/" + this.selectedBucket + "/lists")
        .then((data) => {
          this.groups = data;
        });
    },
  },
  methods: {
    load() {
      http.fetch("/work/times").then((data) => {
        this.data = data;
      });
    },
    refresh() {
      this.load();
    },
    showTimeAllocator(row) {
      if (row.type !== "bucket") {
        return;
      }
      this.selectedRow = row;
      this.$refs.timeAllocatorModal.showModal(row.work_bucket_id);
    },
    timeRange(row) {
      const start = new Date(row.started_at);
      const end = new Date(start.getTime() + row.duration * 1000);

      const format = (d) => d.toTimeString().slice(0, 5);

      return `${format(start)} - ${format(end)}`;
    },
    saveActiveTime(id) {
      http.fetch("/work/times/stop", { id: id }).then((data) => {
        this.data.active = [];
        this.data.duration = data.times.duration;
        this.data.rows = data.times.rows;
      });
    },
    saveTime(saveDuration = false) {
      let duration = 0;
      if (saveDuration) {
        const [hours, minutes] = this.duration.split(":").map(Number);
        duration = (hours * 60 + minutes) * 60;
      }
      let data = {
        duration,
      };
      if (this.newItem.id === 0) {
        data.name = this.newItem.name;
        data.work_task_group_id = this.selectedGroup;
      } else {
        data.work_task_id = this.newItem.id;
      }
      http.fetch("/work/times", data).then((data) => {
        this.data.active = data.active;
        this.newItem = null;
        this.selectedBucket = null;
        this.selectedGroup = null;
      });
    },
    formatDate(val) {
      return date.format(val, false, false);
    },
    formatTime(seconds, secunds = false) {
      if (seconds >= 60) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (secunds) {
          const remainingSeconds = seconds % 60;
          return `${hours}:${minutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
        }
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
      } else {
        return `${seconds} s`;
      }
    },
    getNextMonday(startDate) {
      const date = new Date(startDate);
      while (date.getDay() !== 1) {
        date.setDate(date.getDate() + 1);
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    getWeeksBetweenDates(startDate, endDate) {
      const date = new Date(startDate);
      const date2 = new Date(endDate);

      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date2.setHours(23);
      date2.setMinutes(59);
      date2.setSeconds(59);

      const oneDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.round(
        Math.abs((date.getTime() - date2.getTime()) / oneDay)
      );
      return Math.ceil(diffDays / 7);
    },
    elapsedTime(startDate) {
      const elapsedMilliseconds =
        new Date().getTime() - new Date(startDate).getTime();
      return Math.floor(elapsedMilliseconds / 1000);
    },
    searchTask(search, loading) {
      if (search.length < 1) {
        return;
      }
      loading(true);
      http.fetch("/work/tasks/search?q=" + search).then((data) => {
        this.tasks = data;
      });
      loading(false);
    },
    selectOption(d) {
      this.selectedBucket = {
        id: d.work_bucket_id,
        name: d.bucket,
      };
      this.selectedGroup = {
        id: d.work_task_group_id,
        name: d.group,
      };
    },
    createOption(d) {
      return {
        id: 0,
        name: d,
      };
    },
    getWeekDatesWithDayNames(startDate) {
      const dates = [];
      const currentDate = new Date(startDate);

      const firstDay = new Date(
        currentDate.setDate(
          currentDate.getDate() - ((currentDate.getDay() + 6) % 7)
        )
      );

      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6);

      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];

      for (
        let date = new Date(firstDay);
        date <= lastDay;
        date.setDate(date.getDate() + 1)
      ) {
        const dayIndex = date.getDay();

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        dates.push({
          day: dayNames[dayIndex],
          date: `${year}-${month}-${day}`,
        });
      }

      return dates;
    },
  },
};
</script>
<style scoped>
.week-header {
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc, #eef3fb);
  border-bottom: 1px solid #e7edf7;
}

.time-day {
  padding: 0.6em 1.2em;
  margin: 0.3em;
  border-radius: 6px;
}
.time-today {
  color: #62277d;
  border: 1px solid #a947d6;
}
.time-today .text-muted {
  color: #7d578e !important;
}

.week-totals div {
  flex: 1;
  padding: 6px;
}

.week-totals .value {
  font-weight: 600;
  font-size: 15px;
}

.week-totals .label {
  font-size: 12px;
  color: #6b7280;
}

/* Session list - modern timeline feel */
.tracked-session {
  padding: 14px 18px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e7edf7;
  transition: all 0.25s ease;
  position: relative;
  margin-bottom: 10px;
}

.tracked-session:hover {
  transform: translateY(-2px) scale(1.01);
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.tracked-session.assigned {
  border-left: 4px solid #22c55e;
}

.tracked-session.unassigned {
  border-left: 4px solid #f43f5e;
}

.tracked-session strong {
  font-size: 15px;
}

.text-muted {
  color: #94a3b8 !important;
}
.session-title {
  font-weight: 600;
  font-size: 15px;
}

.session-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-top: 6px;
  color: #64748b;
}

.session-tags {
  margin-top: 8px;
}

.session-tag {
  display: inline-block;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 10px;
  background: #eef2ff;
  color: #4f46e5;
  margin-right: 6px;
}
</style>
